import { ChangeEvent, useMemo } from 'react'

import cn from 'classnames'
import { Loader2 } from 'lucide-react'
import Skeleton from 'react-loading-skeleton'

import TokenSelectionPill from '@src/components/TokenSelectionPill'
import WarningIcon from '@src/components/icons/WarningIcon'
import { NATIVE_TOKEN_ADDRESS } from '@src/configs/chainInfo'
import { CHAIN_ICON_SIZE, MAX_TOKEN_DISPLAY_DECIMALS, TOKEN_ICON_SIZE } from '@src/constants/exchange'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { useExchangeDerivedState } from '@src/pages/Exchange/hooks/useExchangeDerivedState'
import { IExchangeData } from '@src/types/exchange'
import { IQuoteResponse } from '@src/types/order'
import { IToken } from '@src/types/token'
import { Balance, computeUSDAmount, createContextLogger, formatNumberStringWithCommas } from '@src/utils'

/**
 * Props for the FromSection
 */
interface IFromSectionProps {
    sellData: IExchangeData
    buyData: IExchangeData
    /**
     * Whether the user's wallet is connected to the correct chain for this "sellData".
     * If false, we might show a disabled input or hide the 'Max' button, etc.
     */
    isCorrectWalletConnected: boolean

    /** If the user's balance < sellData.value, show an "Insufficient" style. */
    isInsufficientBalance: boolean

    /**
     * If your app is computing gas, you might show a spinner on the Max button
     * for native tokens.
     */
    isComputingGas: boolean
    isGasPrecomputingDone: boolean

    /**
     * The parent container's local string for the numeric input.
     * e.g. '0.002'
     */
    sellAmountString: string

    quoteResponse: IQuoteResponse

    /** Called whenever the user types in the input. */
    onChangeSellValue: (e: ChangeEvent<HTMLInputElement>) => void

    /** Called when the user clicks "Max". */
    onSetMax: () => void

    onTokenSelectionChange: (
        newData: { token?: IToken; chainId?: number; value?: bigint },
        type: 'buy' | 'sell'
    ) => void
}

const logger = createContextLogger('FromSection')

/**
 * FromSection:
 * - "From" label
 * - TokenSelectionPill for selecting the sell token
 * - Input for typing the amount
 * - Shows "Bal: ..." + "Max" button
 * - Optionally an icon or tooltip if token is unwrapped
 */

export function FromSection({
    sellData,
    buyData,
    isCorrectWalletConnected,
    isInsufficientBalance,
    isComputingGas,
    isGasPrecomputingDone,
    sellAmountString,
    onChangeSellValue,
    onSetMax,
    onTokenSelectionChange,
}: IFromSectionProps) {
    const { isSellBalanceLoading, sellBalance, sellBalanceStabilized } = useExchangeBalances()
    const { disabledFromChains: disabledChains } = useExchangeDerivedState()
    const { hasInsufficientGas, gasAmountNeededString } = useExchangeState()

    const sellBalanceInUSD = useMemo<string | undefined>(() => {
        const parsedValue = new Balance(sellData.token).parseString(sellAmountString).bigintValue

        const a = computeUSDAmount(sellData.token, parsedValue)

        if (a === undefined) {
            return undefined
        } else {
            return `$${formatNumberStringWithCommas(a.toString())}`
        }
    }, [sellData.token, sellData.token.decimals, sellData.value, sellAmountString])

    const maxButtonIsLoading = useMemo(() => {
        return (
            sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId] &&
            (isComputingGas || !isGasPrecomputingDone)
        )
    }, [sellData.token, sellData.chainId, isComputingGas, isGasPrecomputingDone])

    const BalanceAndMaxButton = (
        <>
            {isSellBalanceLoading || !sellBalance || !sellBalanceStabilized ? (
                <Skeleton containerClassName='w-24' enableAnimation baseColor='#9B9B9B' />
            ) : (
                <>
                    <span className={cn(isInsufficientBalance && 'text-error')}>
                        Bal:{' '}
                        {sellBalance !== undefined ? sellBalance.formatWithMagnitudes(MAX_TOKEN_DISPLAY_DECIMALS) : 0}{' '}
                        {sellData.token.symbol}
                    </span>
                    <button
                        className='rounded-md border-mach-button-tabs-border bg-mach-button-tabs-background px-2 py-1'
                        onClick={onSetMax}
                        disabled={maxButtonIsLoading}
                    >
                        {maxButtonIsLoading && <Loader2 className='mr-1 inline h-3 w-3 animate-spin' />}
                        Max
                    </button>
                </>
            )}
        </>
    )

    return (
        <div className='space-y-4 rounded-3xl border-mach-surface-border bg-mach-surface-background p-5'>
            <div className='flex items-center justify-between'>
                {/* Label row */}

                <span className='text-sm text-mach-accent-forground'>Sell</span>
                <div className='flex items-center gap-2'>
                    {sellData.token?.address === NATIVE_TOKEN_ADDRESS[sellData.chainId] && (
                        <span
                            className='flex h-4 w-4 items-center justify-center rounded-full bg-primary/50 p-2 text-[10px] text-white'
                            data-tooltip-id='unwrapped-token-notification-tooltip'
                        >
                            !
                        </span>
                    )}
                    <TokenSelectionPill
                        onChange={_d => onTokenSelectionChange(_d, 'sell')}
                        value={sellData}
                        showNetwork
                        tokenIconSize={TOKEN_ICON_SIZE}
                        chainIconSize={CHAIN_ICON_SIZE}
                        disabledChains={disabledChains}
                        buyData={buyData}
                        sellData={sellData}
                        side='sell'
                    />
                </div>
            </div>

            {/* Token selection pill & numeric input */}
            <div className='flex items-center justify-between'>
                <input
                    className={cn(
                        'w-full bg-transparent text-left text-lg md:text-[32px]',
                        isCorrectWalletConnected && isInsufficientBalance && 'text-error'
                    )}
                    placeholder='0'
                    value={sellAmountString}
                    onChange={e => {
                        logger.info('should call onChangeSellValue')
                        onChangeSellValue(e)
                    }}
                    inputMode='decimal'
                    required
                />

                <TokenSelectionPill
                    onChange={_d => onTokenSelectionChange(_d, 'sell')}
                    value={sellData}
                    tokenIconSize={TOKEN_ICON_SIZE}
                    chainIconSize={CHAIN_ICON_SIZE}
                    disabledChains={disabledChains}
                    buyData={buyData}
                    sellData={sellData}
                    side='sell'
                />
            </div>

            <div className='flex items-center justify-between'>
                {/* Last row: usd value of sellData */}
                <span className='md:text-md flex items-center justify-end gap-4 text-xs text-mach-sub-text sm:text-sm'>
                    {sellBalanceInUSD ?? <></>}
                </span>

                {/* Balance & Max row */}
                <div>
                    {isCorrectWalletConnected && (
                        <span className='md:text-md flex items-center justify-end gap-1 text-xs text-mach-sub-text sm:text-sm'>
                            {BalanceAndMaxButton}
                        </span>
                    )}
                </div>
            </div>

            {hasInsufficientGas && isCorrectWalletConnected && !isSellBalanceLoading && sellBalanceStabilized && (
                <div className='md:text-md flex items-center justify-start gap-4 text-xs text-mach-sub-text sm:text-sm'>
                    <WarningIcon></WarningIcon> At least {gasAmountNeededString} is required to execute this swap
                </div>
            )}
        </div>
    )
}
